import { decode } from 'jsonwebtoken'
import { create, ApiResponse } from 'apisauce'

import { all, call, delay, takeLatest, Payload, put } from 'redux-saga/effects'

import { setSchool } from '~/store/modules/user/actions'
import {
  setProfile,
  profiles as setProfiles
} from '~/store/modules/profile/actions'
import { productRequest } from '~/store/modules/products/actions'
import {
  loading,
  enableRefreshTokenMiddleware
} from '~/store/modules/global/actions'
import {
  saveReducedOldToken,
  setSigned,
  signInFailure,
  signInRequestLoading
} from '~/store/modules/auth/actions'
import { checkTermsRequest } from '~/store/modules/acceptTerms/actions'
import { store } from '~/store'

import { toast } from '@psdhub/common/utils'
import { setAuthorization, removeAuthorization } from '@psdhub/api'

import history from '~/services/history'
// import { changeSchool, ApiChange } from '~/services/eemIntegration'
// import { EEMConnectPost } from '~/services/eemConnect'

import { clearStrikes, getStrikesCount, storeStrike } from '~/utils/reCaptcha'
import { isTokenExpired } from '~/utils/isTokenExpired'

import refreshTokenMiddleware from '~/middlewares/refreshToken'

import {
  SignInRequest,
  AuthApi,
  // RefreshTokenApi,
  RehydrateAuth,
  AccessData
} from './types'
import {
  Actions,
  refreshTokenRequest,
  signInSuccess,
  signOut,
  // refreshTokenSuccess,
  reducedTokenEEM
} from './actions'
import { notificationsRequest } from '../notifications/actions'

type SignInPayload = Payload<SignInRequest>

const headers = {
  headers: {
    ContentType: 'multipart/form-data'
  }
}

export const loginApi = create({
  baseURL: process.env.REACT_APP_API_URL
})

export function* signIn({ payload }: SignInPayload): Generator {
  try {
    const redirectTo = payload.redirect
    delete payload.redirect

    yield put(signInRequestLoading())

    const formData = new FormData()
    formData.append('grant_type', 'password')
    formData.append('password', payload.password)
    formData.append('username', payload.username)

    const response = yield call(async () => {
      return loginApi.post('/login/token', formData, headers)
    })

    const { data } = response as ApiResponse<AuthApi>

    const dados = data?.dados

    const user = decode(dados?.access_token as string) as any

    if (data?.dados.access_token) {
      clearStrikes()
    }

    yield put(
      signInSuccess({
        token: dados?.access_token,
        refresh_token: dados?.refresh_token,
        old_token: dados?.access_token_parceiro,
        exp: user?.exp,
        info: {
          ...user,
          guid: user?.sub,
          username: user?.username,
          email_salas: '',
          name: user.name,
          schools: dados?.schools
        }
      })
    )

    if (redirectTo) {
      history.push(`/perfil?redirect=${redirectTo}`)
      return
    }

    history.push('/perfil')
  } catch {
    storeStrike()

    const genericMessage =
      'Usuário ou senha inválidos, verifique seus dados e tente novamente!'

    toast.error(genericMessage)

    if (getStrikesCount() > 1 && payload.openExpiredPwdModal) {
      payload.openExpiredPwdModal()
    }

    return yield put(signInFailure())
  }
}

/**
 * Saga que cuida dos side effects da ação de logout do usuário,
 * remove token de autorização dos headers padrão do cliente http
 * e redireciona o usuário para tela de login
 */
export function* prepareSignOut(): Generator {
  removeAuthorization('all')

  yield delay(500)
  history.push('/login')
}

/*
  !Evento disparado no login inicial do usuário
  !e também no processo de alteração de escola/perfil
*/
type PreparingAccessPayload = Payload<AccessData>
export function* prepareAccess({ payload }: PreparingAccessPayload): Generator {
  const { profiles, selected_profile, selected_school, redirect } = payload

  const { exp, refresh_token, reduced_token, token } = store.getState().auth

  yield put(loading(true))

  yield call(async () => {
    return refreshTokenMiddleware({
      exp,
      refresh_token,
      reduced_token,
      dispatch: store.dispatch
    })
  })

  yield put(setSchool(selected_school))

  const formData = new FormData()
  formData.append('grant_type', 'change_school')
  formData.append('access_token', token || '')
  formData.append('school_id', selected_school.id)

  const response = yield call(async () => {
    return loginApi.post('/login/token', formData, headers)
  })

  const { data } = response as ApiResponse<AuthApi>
  const dados = data?.dados

  const accessToken = dados?.access_token
  const reducedOldToken = dados?.access_token_parceiro

  setAuthorization(accessToken || '', 'all')

  const { info: user, school } = store.getState().user

  const tokenDecode = decode(accessToken || '') as any

  if (!accessToken) {
    toast.error(
      'Algo deu errado ao preparar o seu acesso, tente novamente mais tarde'
    )

    yield put(loading(false))

    return yield put(signOut())
  }

  if (tokenDecode?.sub && user?.guid !== tokenDecode?.sub) {
    const sc = school?.label as string
    toast.error(`Você não tem acesso a escola: ${sc}`)

    yield put(loading(false))

    return yield put(signOut())
  }

  yield put(reducedTokenEEM(accessToken))
  yield put(saveReducedOldToken(reducedOldToken))

  yield put(
    setProfile({
      colorProfile: selected_profile.colorProfile,
      guid: selected_profile.id,
      name: selected_profile.name,
      profile: selected_profile.icon
    })
  )

  yield put(setProfiles(profiles))

  if (Number(process.env.REACT_APP_FEATURE_NOTIFICATIONS)) {
    yield put(notificationsRequest())
  }

  yield put(enableRefreshTokenMiddleware(true))

  if (redirect) {
    yield put(setSigned())

    history.push(redirect)
  }

  yield put(checkTermsRequest())

  return yield put(loading(false))
}

/*
  Acionado no ciclo de vida primário da aplicação
  Buscando o token e chamando os produtos
*/
type ExpiringRehydrate = Payload<RehydrateAuth>
export function* checkingExpiringToken({
  payload
}: ExpiringRehydrate): Generator {
  if (!payload) return

  if (!payload.auth.signed && payload.auth.token) {
    return yield put(signOut())
  }

  const { exp, reduced_token } = payload.auth

  if (exp === 0) return

  if (isTokenExpired(exp)) {
    return yield put(refreshTokenRequest())
  }

  yield put(loading(true))

  setAuthorization(reduced_token, 'all')

  yield put(reducedTokenEEM(reduced_token))

  yield put(enableRefreshTokenMiddleware(true))

  yield delay(1500)

  yield put(checkTermsRequest())

  return yield put(productRequest({}))
}

/*
  Realiza do refresh do token e gera um novo
  token reduzido para transição entre as soluções
  do tipo EEM e Studos
*/
export function* refreshToken(): Generator {
  // const { refresh_token } = store.getState().auth

  // const response = yield call(() => {
  //   return EEMConnectPost({
  //     endpoint: 'connect/token',
  //     data: {
  //       refresh_token: refresh_token,
  //       grant_type: 'refresh_token'
  //     }
  //   })
  // })

  // const { data, ok } = response as ApiResponse<RefreshTokenApi>

  // if (!ok) {
  toast.warn('Sua sessão expirou! Faça o login novamente para continuar!')

  yield put(signOut())

  return history.push('/login')
  // }

  // const res = yield call(async () => {
  //   return changeSchool({
  //     token: data?.access_token
  //   })
  // })

  // const { access_token } = res as ApiChange

  // yield put(reducedTokenEEM(access_token))

  // setAuthorization(access_token, 'all')

  // const user = decode(data?.access_token as string) as {
  //   exp: number
  //   sub: string
  // }

  // yield put(
  //   refreshTokenSuccess({
  //     refresh_token: data?.refresh_token as string,
  //     token: data?.access_token as string,
  //     exp: user?.exp
  //   })
  // )

  // yield put(enableRefreshTokenMiddleware(true))

  // yield delay(2000)

  // return yield put(productRequest({}))
}

export default all([
  takeLatest(Actions.SIGN_IN_REQUEST, signIn),
  takeLatest(Actions.SIGN_OUT, prepareSignOut),
  takeLatest(Actions.REHYDRATE, checkingExpiringToken),
  takeLatest(Actions.FIRST_ACCESS, prepareAccess),
  takeLatest(Actions.REFRESH_TOKEN_REQUEST, refreshToken)
])
